import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Trash2, ArrowLeft } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";

// Página pública de exclusão de conta e dados — exigida pelo Google Play
// (campo "URL para exclusão de contas" em Segurança dos Dados). Precisa
// estar acessível SEM login (pra revisão do Google conseguir ver a
// política), mas a exclusão em si só roda se o usuário estiver logado.
export default function DeleteAccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [done, setDone] = useState(false);

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!confirmChecked) {
      toast.error("Marque a confirmação de que entende que essa ação é irreversível.");
      return;
    }
    if (confirmEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase()) {
      toast.error("O e-mail digitado não confere com o da sua conta.");
      return;
    }
    setDeleting(true);
    try {
      await api.post("/account/delete", { confirm_email: confirmEmail.trim() });
      setDone(true);
      toast.success("Conta e dados excluídos com sucesso.");
      setTimeout(() => {
        logout();
        navigate("/");
      }, 3000);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao excluir a conta. Tente novamente."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0f", color: "#f4f4f5", padding: "40px 20px" }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#a1a1aa", fontSize: 13, marginBottom: 24, textDecoration: "none" }}>
          <ArrowLeft size={14} /> Voltar pro AutoPost BR
        </Link>

        <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>Exclusão de conta e dados</h1>
        <p style={{ fontSize: 14, color: "#a1a1aa", marginBottom: 28 }}>
          AutoPost BR — desenvolvedor AutoPost BR
        </p>

        {/* Etapas exigidas pelo Google Play na descrição do link */}
        <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Como solicitar a exclusão</h2>
          <ol style={{ fontSize: 13.5, color: "#d4d4d8", lineHeight: 1.8, paddingLeft: 20 }}>
            <li>Faça login na sua conta do AutoPost BR (mesmo e-mail/senha do app ou site)</li>
            <li>Nessa mesma página, digite seu e-mail de confirmação</li>
            <li>Marque a caixa de confirmação e clique em "Excluir minha conta e todos os dados"</li>
            <li>Pronto — a exclusão é executada na hora, sem etapa adicional</li>
          </ol>
        </div>

        <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>O que é excluído</h2>
          <ul style={{ fontSize: 13.5, color: "#d4d4d8", lineHeight: 1.8, paddingLeft: 20 }}>
            <li>Sua conta (e-mail, nome, senha) e login param de funcionar imediatamente</li>
            <li>Conexão do WhatsApp (a instância é desconectada e apagada da Evolution API)</li>
            <li>Conexão com Meta Ads (Facebook/Instagram) e dados de campanhas salvos</li>
            <li>Contatos e mensagens do Mailing, configurações do Agente de Prospecção, conversas de leads</li>
            <li>Links e cliques de UTM, histórico de vendas atribuídas</li>
          </ul>
          <p style={{ fontSize: 13, color: "#71717a", marginTop: 12, lineHeight: 1.6 }}>
            A exclusão é <strong style={{ color: "#f4f4f5" }}>imediata e permanente</strong>, sem período de retenção adicional.
            Registros financeiros que a lei brasileira exige manter (ex: notas fiscais e histórico de cobrança, quando aplicável)
            podem ser preservados pelo prazo legal, dissociados dos seus dados de uso do produto.
          </p>
        </div>

        {/* Ação de exclusão — só funciona logado */}
        <div style={{ background: "#18181b", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: 20 }}>
          {done ? (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <Trash2 size={32} color="#22c55e" style={{ margin: "0 auto 12px" }} />
              <p style={{ fontWeight: 700 }}>Conta excluída com sucesso.</p>
              <p style={{ fontSize: 13, color: "#a1a1aa", marginTop: 6 }}>Redirecionando...</p>
            </div>
          ) : !user ? (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <AlertTriangle size={24} color="#f59e0b" style={{ margin: "0 auto 10px" }} />
              <p style={{ fontSize: 14, marginBottom: 14 }}>Você precisa estar logado pra excluir sua conta.</p>
              <Button onClick={() => navigate("/login?redirect=/excluir-conta")} className="bg-blue-600 hover:bg-blue-500">
                Fazer login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleDelete}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14 }}>
                <AlertTriangle size={18} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
                <p style={{ fontSize: 13.5, color: "#fca5a5" }}>
                  Essa ação é <strong>irreversível</strong>. Todos os dados listados acima serão apagados
                  permanentemente da conta <strong>{user.email}</strong>.
                </p>
              </div>

              <label style={{ fontSize: 12.5, color: "#a1a1aa", display: "block", marginBottom: 6 }}>
                Digite seu e-mail ({user.email}) pra confirmar
              </label>
              <Input
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder={user.email}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mb-4"
              />

              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#d4d4d8", marginBottom: 18, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                Entendo que essa ação é permanente e não pode ser desfeita.
              </label>

              <Button
                type="submit"
                disabled={deleting}
                className="w-full bg-red-600 hover:bg-red-500"
              >
                {deleting ? "Excluindo..." : "🗑️ Excluir minha conta e todos os dados"}
              </Button>
            </form>
          )}
        </div>

        <p style={{ fontSize: 12, color: "#52525b", marginTop: 24, textAlign: "center" }}>
          Dúvidas? Fale com a gente antes de excluir: <a href="mailto:suporte@autopostbr.com.br" style={{ color: "#71717a" }}>suporte@autopostbr.com.br</a>
        </p>
      </div>
    </div>
  );
}
